import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { integrationSources } from '../../../../../../../extViewmodels/Utils';
import { useUserSession } from '../../../../../../../models/hooks/appStateHooks';
import { contactIsFromEnabledIntegration } from '../../../../../../../models/UiUtils';
import { useGetExternalIntegrationsQuery } from '../../../../../../../queries/Integrations/useGetExternalIntegrations';
import { EntityInfoField } from '../../../../../../components/entities/EntityInfoField';
import { styleSheet } from '../styles';
import { Address } from './Address/Address';
import { EmailList } from './Email/EmailList';
import { useContactTagsUtils } from './hooks/useContactTagsUtils';
import { PhoneNumberList } from './PhoneNumber/PhoneNumberList';
import { WebsiteList } from './Website/WebsiteList';

type Props = {
	contact: Api.IContact;
	compactLayout?: boolean;
	showSendMessage?: boolean;
	onEmailChanged: (emails: Api.EmailAddress[]) => void;
	onPhoneNumberChanged: (phoneNumbers: Api.IPhoneNumber[]) => void;
	onAddressChanged: (address: Api.IAddress) => void;
	onWebsiteChanged: (website: string) => void;
	refetchContact: () => void;
};

export const ContactDetails = ({
	contact,
	onEmailChanged,
	onPhoneNumberChanged,
	onAddressChanged,
	onWebsiteChanged,
	refetchContact,
}: Props) => {
	const userSession = useUserSession();
	const { onRequestAddTag, onRequestRemoveTag, isLoading } = useContactTagsUtils(contact, refetchContact);

	// Fetch v2 integrations
	const { data: externalIntegrations } = useGetExternalIntegrationsQuery({
		impersonationContext: undefined,
	});

	// Create a contact view model from the contact prop
	const contactVm = React.useMemo(() => {
		return new Api.ContactViewModel(userSession, contact);
	}, [contact, userSession]);

	/**
	 * Helper function to check if a specific integration source is enabled.
	 * Supports both v1 (IAccountIntegrations) and v2 (IExternalIntegration[]) integration formats.
	 * V2 integrations take priority over v1 if found.
	 */
	const isIntegrationSourceEnabled = React.useCallback(
		(source: Api.IntegrationSources): boolean => {
			// First check v2 integrations (IExternalIntegration[]) if provided
			if (externalIntegrations) {
				// IntegrationProvider enum values match IntegrationSources values (e.g., 'HawkSoft', 'AMS360')
				const v2Integration = externalIntegrations.find(
					integration => integration.provider === (source as unknown as Api.IntegrationProvider)
				);
				if (v2Integration) {
					return v2Integration.enabled ?? false;
				}
			}

			// Fall back to v1 integrations (IAccountIntegrations)
			return (
				contactIsFromEnabledIntegration(contactVm, userSession.account.integrations) &&
				integrationSources.includes(source as Api.IntegrationSources)
			);
		},
		[contactVm, userSession.account.integrations, externalIntegrations]
	);

	/**
	 * Determines if the unsubscribe checkbox should be disabled.
	 * Uses the same logic as tag deletion restrictions - if the Unsubscribe tag
	 * comes from an enabled integration, we should disable the checkbox (prevent removal).
	 * We still allow ADDING the unsubscribe tag even for integration contacts.
	 */
	const shouldDisableUnsubscribeCheckbox = React.useMemo((): boolean => {
		const unsubscribeTag = contact.tagsCollection?.find(x => x.name?.toLowerCase() === 'unsubscribe');
		const tagSource = unsubscribeTag?.source;

		// Only disable if the tag exists AND has an integration source
		if (!tagSource || !integrationSources.includes(tagSource as Api.IntegrationSources)) {
			return false;
		}

		return isIntegrationSourceEnabled(tagSource as Api.IntegrationSources);
	}, [contact.tagsCollection, isIntegrationSourceEnabled]);

	const onUnsubscribeFromEmailCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = !!(e.target as HTMLInputElement).checked;
		if (checked) {
			onRequestAddTag({ name: 'Unsubscribe' });
		} else {
			onRequestRemoveTag({ name: 'Unsubscribe' });
		}
	};

	const renderLabeledField = (label: string, value: React.ReactNode, valueClassName?: string, truncateText = true) => {
		return (
			<EntityInfoField
				enableLinkify={true}
				label={label}
				truncateText={truncateText}
				value={value}
				valueClassName={valueClassName}
			/>
		);
	};

	return (
		<div className={css(styleSheet.contactDetailsContainer)}>
			<EmailList
				contact={contact}
				contactVm={contactVm}
				onEmailChanged={onEmailChanged}
				onUnsubscribeFromEmailCheckboxChanged={onUnsubscribeFromEmailCheckboxChanged}
				isUnsubscribing={isLoading}
				disableUnsubscribeCheckbox={shouldDisableUnsubscribeCheckbox}
			/>

			<PhoneNumberList
				contact={contact}
				contactVm={contactVm}
				onPhoneNumberChanged={onPhoneNumberChanged}
				refetchContact={refetchContact}
			/>

			<Address contact={contact} onAddressChanged={onAddressChanged} />

			<WebsiteList contact={contact} onWebsiteChanged={onWebsiteChanged} />

			{/* Misc Section */}
			<div className={css(styleSheet.contactDetailSection)}>
				{renderLabeledField('Misc', contactVm.bio, css(styleSheet.contactDetailBio), false)}
			</div>
		</div>
	);
};
